import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useI18n } from "../i18n";
import { CONTACT } from "../lib/contact";
import { FlagPT, FlagUK } from "./Flags";
import { trackCalendar, trackLanguage } from "../lib/analytics";

const Navbar = () => {
  const { lang, setLang, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const onScroll = useCallback(() => {
    setScrolled(window.scrollY > 30);
  }, [setScrolled]);

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const links = [
    { href: "#services", label: t.nav.services },
    { href: "#how", label: t.nav.how },
    { href: "/guias", label: t.nav.guide },
    { href: "#testimonials", label: t.nav.testimonials },
    { href: "#faq", label: t.nav.faq },
    { href: "#contact", label: t.nav.contact },
  ];

  const isPt = lang === "pt";
  const FlagIcon = isPt ? FlagPT : FlagUK;
  const flagCode = isPt ? "PT" : "EN";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#050507]/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5 group" data-testid="navbar-logo">
          <img src="/logo-da-gold.png" alt="Diogo Almeida" className="w-11 h-11 object-contain drop-shadow-[0_0_18px_rgba(166,0,86,0.4)]" />
          <div className="leading-tight">
            <div className="text-white font-medium tracking-tight">Diogo Almeida</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/50">
              {isPt ? "Diretor de Loja · Gestor de Crédito" : "Branch Manager · Mortgage Advisor"}
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-sm text-white/70 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              const next = isPt ? "en" : "pt";
              setLang(next);
              trackLanguage(next);
            }}
            data-testid="lang-toggle"
            aria-label="Toggle language"
            className="hidden sm:flex items-center gap-2 text-xs text-white/80 hover:text-white transition-colors px-3 py-2 rounded-full border border-white/10 hover:border-white/25"
          >
            <FlagIcon className="w-4 h-3 rounded-[2px] overflow-hidden" />
            <span className="font-medium">{flagCode}</span>
          </button>

          <a
            href={CONTACT.calendar}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackCalendar("navbar")}
            className="hidden md:inline-flex btn-primary text-sm"
            data-testid="navbar-cta"
          >
            {t.nav.cta}
          </a>

          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen(!open)}
            data-testid="navbar-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden bg-[#050507]/95 backdrop-blur-xl border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-white/80 hover:text-white py-2 border-b border-white/5"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => setLang(isPt ? "en" : "pt")}
                  className="flex items-center gap-2 text-xs text-white/80 px-3 py-2 rounded-full border border-white/10"
                  data-testid="lang-toggle-mobile"
                >
                  <FlagIcon className="w-4 h-3" />
                  <span className="font-medium">{flagCode}</span>
                </button>
                <a
                  href={CONTACT.calendar}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setOpen(false)}
                  className="btn-primary text-sm"
                >
                  {t.nav.cta}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
